/** @format */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Button } from 'src/components';
import { ShareModal } from 'src/components/ShareModal';
import ExtranetContentBody from './ExtranetContentBody';
import ExtranetContentActionButtons from './ExtranetContentActionButtons';
import { GA4_COMPONENT_POSITION } from 'src/analytics/componentInteractionGa4';
import useExtranetContentDetailState from './useExtranetContentDetailState';
import useExtranetContentActions from './useExtranetContentActions';
import { PDF_VIEWER_OPEN_CLASS } from './constants';

const CLOSE_ANIMATION_MS = 300;
const SHOW_DRAWER_HEADER = false;
const BODY_SCROLL_LOCK_CLASS = 'topics-content-drawer-open';

export default function ExtranetContentDrawer() {
  const navigate = useNavigate();
  const { contentID, locale } = useParams();
  const { t } = useTranslation();
  const { contentDetail, shouldShowSkeleton, shouldDimDetail, title, hasFatalError } = useExtranetContentDetailState(
    contentID,
    locale
  );
  const { isShareModalOpen, shareUrl, handlePrint, handleShareClick, handleShareModalClose } =
    useExtranetContentActions();
  const [isClosing, setIsClosing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const closeTimerRef = useRef(null);
  const openRafRef = useRef(null);

  useEffect(() => {
    openRafRef.current = window.requestAnimationFrame(() => {
      setIsOpen(true);
    });
    document.body.classList.add(BODY_SCROLL_LOCK_CLASS);
    return () => {
      if (openRafRef.current) {
        window.cancelAnimationFrame(openRafRef.current);
      }
      document.body.classList.remove(BODY_SCROLL_LOCK_CLASS);
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    closeTimerRef.current = setTimeout(() => {
      navigate(-1);
    }, CLOSE_ANIMATION_MS);
  };

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        if (document?.body?.classList?.contains(PDF_VIEWER_OPEN_CLASS)) return;
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  });

  if (hasFatalError) return <Navigate to="/404" replace />;

  return (
    <>
      <div
        className={`topics-content-drawer ${
          isClosing ? 'topics-content-drawer--closing' : isOpen ? 'topics-content-drawer--open' : ''
        }`}
        role="presentation"
        onClick={handleClose}>
        <div
          className="topics-content-drawer__panel"
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onClick={e => e.stopPropagation()}>
          <div className="container container--narrow topics-content-drawer__inner">
            {SHOW_DRAWER_HEADER ? (
              <div className="topics-content-drawer__header">
                <h2 className="topics-content-drawer__title">{title}</h2>
                <button
                  className="topics-content-drawer__close"
                  type="button"
                  onClick={handleClose}
                  aria-label={t('common.button.backbtn')}>
                  ×
                </button>
              </div>
            ) : null}

            <div className="topics-content-drawer__body">
              <ExtranetContentBody
                detail={contentDetail}
                showSkeleton={shouldShowSkeleton}
                isLoadingOverlay={shouldDimDetail}
              />
            </div>

            <div className="topics-content-drawer__actions">
              <div className="topics-content-drawer__actions-left">
                <Button secondary onClick={handleClose} label={t('common.button.backbtn')} />
              </div>
              <div className="topics-content-drawer__actions-right">
                <ExtranetContentActionButtons
                  onPrint={handlePrint}
                  onShare={handleShareClick}
                  className="topics-content-drawer__icon-btn"
                  componentPosition={GA4_COMPONENT_POSITION.EXTRANET_CONTENT_DRAWER}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <ShareModal isOpen={isShareModalOpen} onClose={handleShareModalClose} shareUrl={shareUrl} />
    </>
  );
}
